import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { filterMedia, getGenreOptions, getRegionOptions, getYearOptions } from "@/lib/media";
import type { SortKey } from "@/lib/media";
import type { MediaType } from "@/types/movie";
import { TYPE_LABELS } from "@/types/movie";

const PAGE_SIZE = 8;

const VALID_TYPES: MediaType[] = ["movie", "tv", "variety"];

const CATEGORY_DESC: Record<MediaType, string> = {
  movie: "汇集动作、科幻、爱情、悬疑等多元类型影片，随时开启一段光影旅程。",
  tv: "古装、都市、犯罪、家庭等热门剧集持续更新，陪你追剧不停歇。",
  variety: "音乐、竞技、美食、真人秀，轻松欢乐的综艺节目一站看全。",
};

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "hot", label: "按热度" },
  { key: "new", label: "按时间" },
  { key: "rating", label: "按评分" },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType = VALID_TYPES.includes(type as MediaType) ? (type as MediaType) : null;

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [year, setYear] = useState("all");
  const [region, setRegion] = useState("all");
  const [sort, setSort] = useState<SortKey>("hot");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const label = validType ? TYPE_LABELS[validType] : "";

  useDocumentMeta(
    validType ? `${label} - 91在线观看视频` : "页面不存在 - 91在线观看视频",
    validType ? CATEGORY_DESC[validType] : undefined,
    validType ? `${label},在线观看${label},${label}大全,91在线观看视频` : undefined,
  );

  useEffect(() => {
    setSelectedGenres([]);
    setYear("all");
    setRegion("all");
    setSort("hot");
    setVisibleCount(PAGE_SIZE);
  }, [type]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedGenres, year, region, sort]);

  const results = useMemo(() => {
    if (!validType) return [];
    return filterMedia({ type: validType, genres: selectedGenres, year, region, sort });
  }, [validType, selectedGenres, year, region, sort]);

  const genres = useMemo(() => (validType ? getGenreOptions(validType) : []), [validType]);
  const years = useMemo(() => getYearOptions(), []);
  const regions = useMemo(() => getRegionOptions(), []);

  const visibleResults = results.slice(0, visibleCount);

  const resetFilters = () => {
    setSelectedGenres([]);
    setYear("all");
    setRegion("all");
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) => (prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]));
  };

  if (!validType) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="请从首页或顶部导航进入电影、电视剧或综艺板块。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const filterPanel = (
    <FilterPanel
      genres={genres}
      years={years}
      regions={regions}
      selectedGenres={selectedGenres}
      selectedYear={year}
      selectedRegion={region}
      onToggleGenre={toggleGenre}
      onSelectYear={setYear}
      onSelectRegion={setRegion}
      onReset={resetFilters}
    />
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{label}</span>
      </nav>

      <div className="mb-7">
        <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">最新{label}</h1>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">{CATEGORY_DESC[validType]}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:sticky lg:top-20 lg:block lg:self-start">{filterPanel}</aside>

        <div className="min-w-0 flex-1">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>

            <div className="flex flex-wrap items-center gap-2">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSort(opt.key)}
                  className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    sort === opt.key
                      ? "bg-accent-500 text-primary-950"
                      : "bg-background-100 text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-foreground-500">共 {results.length} 部作品</p>
          </div>

          {visibleResults.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 md:gap-5">
                {visibleResults.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>

              {visibleCount < results.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    加载更多
                    <i className="ri-arrow-down-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              icon="ri-search-eye-line"
              title="暂无符合条件的作品"
              description="试试更换筛选条件，或重置后浏览全部内容。"
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              }
            />
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-primary-950/50"
          ></button>
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-bold text-foreground-950">筛选</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-600 py-3 text-sm font-semibold text-background-50"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}